import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { DestroyRef, inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, forkJoin, interval, of, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LicenseRenewalCandidates, LicenseRenewalWorkflow } from '@workspace-app/odatabackend/QualicoatBackendModel';
import { ToastService } from '@workspace-app/shared';
import {
  ConfirmedCandidateDto,
  ContactInfo,
  CreateRenewalRequest,
  CreateRn1InspectionsResult,
  LicenseRenewalWorkflowDataService,
  NotifyCertificateAvailabilityResult,
  UpdateSelectionRequest,
} from '../services/license-renewal-workflow-data.service';

export interface LicenseRenewalWorkflowState {
  // List view
  reviews: LicenseRenewalWorkflow[];
  reviewsLoading: boolean;
  reviewsError: string | null;

  // Active review
  activeReview: LicenseRenewalWorkflow | null;
  activeReviewLoading: boolean;
  activeReviewError: string | null;

  // Candidates - Step 1: All candidates with inspections for selection
  availableCandidates: LicenseRenewalCandidates[];
  availableCandidatesLoading: boolean;
  availableCandidatesError: string | null;
  selectedCandidateIds: string[];
  refreshingInspections: boolean;

  // Selected candidates - Step 2+: Only candidates where isSelected = true
  selectedCandidates: LicenseRenewalCandidates[];
  selectedCandidatesLoading: boolean;

  // Confirmed candidates - From API with GL contact info
  confirmedCandidates: ConfirmedCandidateDto[];
  confirmedCandidatesLoading: boolean;

  // Workflow state
  currentStep: number;
  isExecuting: boolean;
  certificatesSent: boolean;
  certificateResult: NotifyCertificateAvailabilityResult | null;

  // Certificate recipients
  certificateRecipients: ContactInfo[];
  certificateRecipientsLoading: boolean;
  selectedContactIds: string[];

  // GL notification state
  glNotificationSent: boolean;
  glNotificationResult: { glsNotified: number; certificatesCount: number } | null;

  // RN1 creation state
  rn1CreationInProgress: boolean;
  rn1CreationCompleted: boolean;
  rn1CreationResult: CreateRn1InspectionsResult | null;


  // Certificate generation status
  certificateGenerationStatus: {
    totalCandidates: number;
    pendingCount: number;
    processingCount: number;
    completedCount: number;
    failedCount: number;
    queuedCount: number;
  } | null;
  certificateGenerationStatusLoading: boolean;

  // UI state
  showCreateDialog: boolean;
  showCancelDialog: boolean;
  unsavedChanges: boolean;
  lastCreatedReviewId: string | null;
}

// Helper function to determine step from state
function getStepFromState(state: string): number {
  const stateStepMap: { [key: string]: number } = {
    Created: 1,
    Review: 2,
    IssuingCertificates: 3,
    ReadyToCreateRn1: 4,
    FinalReview: 5,
    Completed: 6,
    Cancelled: 0,
  };
  return stateStepMap[state] || 0;
}

// Helper to extract RN1 result from review entity
function extractRn1Result(review: LicenseRenewalWorkflow): CreateRn1InspectionsResult | null {
  if (!review.rn1CreationResult) return null;

  return {
    successCount: review.rn1CreationResult.successCount || 0,
    failureCount: review.rn1CreationResult.failureCount || 0,
    errors: review.rn1CreationResult.errors || [],
    createdInspectionIds: review.rn1CreationResult.createdInspectionIds?.map((id) => id.toString()) || [],
  };
}

const initialState: LicenseRenewalWorkflowState = {
  reviews: [],
  reviewsLoading: false,
  reviewsError: null,

  activeReview: null,
  activeReviewLoading: false,
  activeReviewError: null,

  availableCandidates: [],
  availableCandidatesLoading: false,
  availableCandidatesError: null,
  selectedCandidateIds: [],
  refreshingInspections: false,

  selectedCandidates: [],
  selectedCandidatesLoading: false,

  confirmedCandidates: [],
  confirmedCandidatesLoading: false,

  currentStep: 0,
  isExecuting: false,
  certificatesSent: false,
  certificateResult: null,

  certificateRecipients: [],
  certificateRecipientsLoading: false,
  selectedContactIds: [],

  glNotificationSent: false,
  glNotificationResult: null,

  rn1CreationInProgress: false,
  rn1CreationCompleted: false,
  rn1CreationResult: null,

  certificateGenerationStatus: null,
  certificateGenerationStatusLoading: false,

  showCreateDialog: false,
  showCancelDialog: false,
  unsavedChanges: false,
  lastCreatedReviewId: null,
};

export const LicenseRenewalWorkflowStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (store, dataService = inject(LicenseRenewalWorkflowDataService), toastService = inject(ToastService)) => ({
      // Load all reviews
      loadReviews: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { reviewsLoading: true, reviewsError: null })),
          switchMap(() =>
            dataService.getAllReviews().pipe(
              tapResponse({
                next: (reviews) => {
                  patchState(store, {
                    reviews,
                    reviewsLoading: false,
                  });
                },
                error: (error: unknown) => {
                  const message = (error as Error)?.message || 'Failed to load reviews';
                  patchState(store, {
                    reviewsLoading: false,
                    reviewsError: message,
                  });
                  toastService.displayErrorMessage(message);
                },
              })
            )
          )
        )
      ),

      // Load single review
      loadReview: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { activeReviewLoading: true, activeReviewError: null })),
          switchMap((reviewId) =>
            dataService.getReview(reviewId).pipe(
              tapResponse({
                next: (review) => {
                  const rn1Result = extractRn1Result(review);
                  patchState(store, {
                    activeReview: review,
                    activeReviewLoading: false,
                    currentStep: getStepFromState(review.currentState),
                    rn1CreationResult: rn1Result,
                    rn1CreationCompleted: !!rn1Result,
                  });
                },
                error: (error: unknown) => {
                  const message = (error as Error)?.message || 'Failed to load review';
                  patchState(store, {
                    activeReviewLoading: false,
                    activeReviewError: message,
                  });
                  toastService.displayErrorMessage(message);
                },
              })
            )
          )
        )
      ),

      // Create new review
      createReview: rxMethod<CreateRenewalRequest>(
        pipe(
          tap(() => patchState(store, { activeReviewLoading: true })),
          switchMap((request) =>
            dataService.createReview(request).pipe(
              tapResponse({
                next: (result) => {
                  const reviewResult = result as { reviewId: string };
                  patchState(store, {
                    activeReviewLoading: false,
                    showCreateDialog: false,
                    lastCreatedReviewId: reviewResult.reviewId,
                  });
                  toastService.displaySuccessMessage('License Renewal created successfully');
                },
                error: (error: unknown) => {
                  // Handle both error.error (from backend BadRequest) and error.message
                  const errorObj = error as { error?: { error?: string; message?: string }; message?: string };
                  const message =
                    errorObj?.error?.error ||
                    errorObj?.error?.message ||
                    errorObj?.message ||
                    'Failed to create review';
                  patchState(store, {
                    activeReviewLoading: false,
                  });
                  toastService.displayErrorMessage(message);
                },
              })
            )
          )
        )
      ),

      // Load available candidates for existing review (Step 1)
      loadCandidates: rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, {
              availableCandidatesLoading: true,
              availableCandidatesError: null,
            })
          ),
          switchMap((reviewId) =>
            dataService.getCandidates(reviewId).pipe(
              tapResponse({
                next: (candidates) => {
                  // Update selectedCandidateIds based on isSelected property from backend
                  const selectedIds = candidates.filter((c) => c.isSelected).map((c) => c.id);

                  patchState(store, {
                    availableCandidates: candidates,
                    selectedCandidateIds: selectedIds,
                    availableCandidatesLoading: false,
                    availableCandidatesError: null,
                  });
                },
                error: (error: unknown) => {
                  let message = 'Failed to load candidates';
                  const errorObj = error as { status?: number; error?: { message?: string }; message?: string };
                  if (errorObj?.status === 404) {
                    message = 'No candidates found for this review';
                  } else if (errorObj?.error?.message) {
                    message = errorObj.error.message;
                  } else if (typeof errorObj?.message === 'string' && !errorObj.message.includes('Http failure')) {
                    message = errorObj.message;
                  }

                  patchState(store, {
                    availableCandidatesLoading: false,
                    availableCandidatesError: message,
                  });

                  // Only show toast for non-404 errors or if there should be candidates
                  const errorStatus = (error as { status?: number })?.status;
                  if (errorStatus !== 404) {
                    toastService.displayErrorMessage(message);
                  }
                },
              })
            )
          )
        )
      ),

      // Select candidates - loads all candidates with inspections for Step 1
      selectCandidates: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, {
              availableCandidatesLoading: true,
              availableCandidatesError: null,
            })
          ),
          switchMap(() => {
            const reviewId = store.activeReview()?.id;
            if (!reviewId) {
              return of({ error: 'No active review' });
            }
            return dataService.selectCandidates(reviewId).pipe(
              switchMap((result) =>
                // After identifying, load the candidates WITH inspections
                dataService.getCandidates(reviewId).pipe(
                  switchMap((candidates) =>
                    // Reload the review to get updated state from backend
                    dataService.getReview(reviewId).pipe(
                      tapResponse({
                        next: (review) => {
                          // Update both candidates and review state
                          patchState(store, {
                            availableCandidates: candidates,
                            availableCandidatesLoading: false,
                            activeReview: review,
                            currentStep: 2,
                            // Use the backend's isSelected property (candidates with 2+ successful inspections)
                            selectedCandidateIds: candidates.filter((c) => c.isSelected).map((c) => c.id),
                          });

                          // Show appropriate message based on result
                          const resultTyped = result as { totalCandidates: number; eligibleCandidates: number };
                          if (resultTyped.totalCandidates === 0) {
                            toastService.displaySuccessMessage(
                              'No candidates found for the specified criteria. You can continue with the workflow.'
                            );
                          } else if (resultTyped.eligibleCandidates === 0) {
                            toastService.displaySuccessMessage(
                              `Found ${resultTyped.totalCandidates} candidates, but none are eligible for extension.`
                            );
                          } else {
                            toastService.displaySuccessMessage(
                              `Identified ${resultTyped.totalCandidates} candidates, ${resultTyped.eligibleCandidates} eligible for extension.`
                            );
                          }
                        },
                        error: (error: unknown) => {
                          const errorObj = error as { error?: { error?: string; message?: string }; message?: string };
                          const message =
                            errorObj?.error?.error ||
                            errorObj?.error?.message ||
                            errorObj?.message ||
                            'Failed to reload review';
                          toastService.displayErrorMessage(message);
                        },
                      })
                    )
                  ),
                  catchError((error: unknown) => {
                    const errorObj = error as { error?: { error?: string; message?: string }; message?: string };
                    const message =
                      errorObj?.error?.error ||
                      errorObj?.error?.message ||
                      errorObj?.message ||
                      'Failed to load candidates';
                    patchState(store, {
                      availableCandidatesLoading: false,
                      availableCandidatesError: message,
                    });
                    toastService.displayErrorMessage(message);
                    return of(null);
                  })
                )
              ),
              catchError((error: unknown) => {
                const errorObj = error as {
                  status?: number;
                  error?: { error?: string; message?: string };
                  message?: string;
                };
                const message =
                  errorObj?.error?.error ||
                  errorObj?.error?.message ||
                  errorObj?.message ||
                  (errorObj?.status === 404 ? 'No candidates found for this review' : 'Failed to identify candidates');
                patchState(store, {
                  availableCandidatesLoading: false,
                  availableCandidatesError: message,
                });
                toastService.displayErrorMessage(message);
                return EMPTY;
              })
            );
          })
        )
      ),

      // Update candidate selection
      updateSelection: rxMethod<{ candidateId: string; isSelected: boolean }>(
        pipe(
          tap(({ candidateId, isSelected }) => {
            const currentSelection = store.selectedCandidateIds();
            let newSelection: string[];

            if (isSelected && !currentSelection.includes(candidateId)) {
              newSelection = [...currentSelection, candidateId];
            } else if (!isSelected && currentSelection.includes(candidateId)) {
              newSelection = currentSelection.filter((id) => id !== candidateId);
            } else {
              return;
            }

            patchState(store, {
              selectedCandidateIds: newSelection,
              unsavedChanges: true,
            });
          })
        )
      ),

      // Save selection to backend
      saveSelection: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { availableCandidatesLoading: true })),
          switchMap(() => {
            const reviewId = store.activeReview()?.id;
            const selectedIds = store.selectedCandidateIds();
            const candidates = store.availableCandidates();

            if (!reviewId) {
              return of({ error: 'No active review' });
            }

            const request: UpdateSelectionRequest = {
              selections: candidates.map((c) => ({
                licenseId: c.licenseId,
                isSelected: selectedIds.includes(c.id),
                notes: '',
              })),
            };

            return dataService.updateSelection(reviewId, request).pipe(
              switchMap((_result) => {
                // After saving, reload candidates to get the updated state
                return dataService.getCandidates(reviewId).pipe(
                  tapResponse({
                    next: (updatedCandidates) => {
                      // Update selectedCandidateIds based on isSelected property from backend
                      const newSelectedIds = updatedCandidates.filter((c) => c.isSelected).map((c) => c.id);

                      patchState(store, {
                        availableCandidates: updatedCandidates,
                        selectedCandidateIds: newSelectedIds,
                        availableCandidatesLoading: false,
                        unsavedChanges: false,
                      });
                      toastService.displaySuccessMessage('Selection updated successfully');
                    },
                    error: (_error: unknown) => {
                      // Even if reload fails, the save was successful
                      patchState(store, {
                        availableCandidatesLoading: false,
                        unsavedChanges: false,
                      });
                      toastService.displaySuccessMessage('Selection updated successfully');
                    },
                  })
                );
              }),
              catchError((error: unknown) => {
                const message = (error as Error)?.message || 'Failed to save selection';
                patchState(store, {
                  availableCandidatesLoading: false,
                });
                toastService.displayErrorMessage(message);
                return EMPTY;
              })
            );
          })
        )
      ),

      // Refresh inspections data for candidates
      refreshInspections: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { refreshingInspections: true })),
          switchMap(() => {
            const reviewId = store.activeReview()?.id;
            if (!reviewId) {
              patchState(store, { refreshingInspections: false });
              return EMPTY;
            }

            return dataService.refreshCandidatesInspections(reviewId).pipe(
              switchMap((result) => {
                // After refreshing, reload candidates to get updated data
                return dataService.getCandidates(reviewId).pipe(
                  tapResponse({
                    next: (updatedCandidates) => {
                      // Maintain current selection state
                      const currentSelectedIds = store.selectedCandidateIds();

                      patchState(store, {
                        availableCandidates: updatedCandidates,
                        selectedCandidateIds: currentSelectedIds, // Preserve selection
                        refreshingInspections: false,
                      });

                      toastService.displaySuccessMessage(
                        `Refreshed inspections: ${result.candidatesUpdated} candidates updated with ${result.totalInspections} total inspections`
                      );
                    },
                    error: (error: unknown) => {
                      const message = (error as Error)?.message || 'Failed to refresh inspections';
                      patchState(store, {
                        refreshingInspections: false,
                      });
                      toastService.displayErrorMessage(message);
                    },
                  })
                );
              }),
              catchError((error: unknown) => {
                const errorObj = error as { error?: { error?: string }; message?: string };
                const message = errorObj?.error?.error || errorObj?.message || 'Failed to refresh inspections';
                patchState(store, {
                  refreshingInspections: false,
                });
                toastService.displayErrorMessage(message);
                return EMPTY;
              })
            );
          })
        )
      ),

      // Confirm selection
      confirmSelection: rxMethod<string | undefined>(
        pipe(
          tap(() => patchState(store, { activeReviewLoading: true })),
          switchMap((notes) => {
            const reviewId = store.activeReview()?.id;
            if (!reviewId) {
              return of({ error: 'No active review' });
            }

            // First, save any unsaved selection changes before confirming
            const hasUnsavedChanges = store.unsavedChanges();
            if (hasUnsavedChanges) {
              const selectedIds = store.selectedCandidateIds();
              const candidates = store.availableCandidates();

              const request: UpdateSelectionRequest = {
                selections: candidates.map((c) => ({
                  licenseId: c.licenseId,
                  isSelected: selectedIds.includes(c.id),
                  notes: '',
                })),
              };

              // Save selection first, then confirm
              return dataService.updateSelection(reviewId, request).pipe(
                tap(() => {
                  // Mark changes as saved after successful update
                  patchState(store, {
                    unsavedChanges: false,
                  });
                }),
                switchMap(() => dataService.confirmSelection(reviewId, notes))
              );
            }

            // No unsaved changes, proceed directly with confirmation
            return dataService.confirmSelection(reviewId, notes);
          }),
          switchMap((result) => {
            const reviewId = store.activeReview()?.id;
            if (!reviewId) {
              return of(result);
            }

            // After confirming, reload the review to get the updated state
            return dataService.getReview(reviewId).pipe(
              tapResponse({
                next: (updatedReview) => {
                  const rn1Result = extractRn1Result(updatedReview);
                  patchState(store, {
                    activeReview: updatedReview,
                    activeReviewLoading: false,
                    currentStep: getStepFromState(updatedReview.currentState),
                    rn1CreationResult: rn1Result,
                    rn1CreationCompleted: !!rn1Result,
                  });
                  const resultTyped = result as { selectedCount: number };
                  toastService.displaySuccessMessage(
                    `Selection confirmed: ${resultTyped.selectedCount} licenses ready for extension`
                  );

                  // Load certificate generation status and selected candidates
                  if (updatedReview.currentState === 'IssuingCertificates') {
                    // Load certificate generation status and processed licenses in parallel
                    dataService.getCertificateGenerationStatus(reviewId).subscribe({
                      next: (status) => {
                        patchState(store, {
                          certificateGenerationStatus: status,
                          certificateGenerationStatusLoading: false,
                        });
                      },
                      error: (error) => {
                        console.error('[Certificate Status] Failed to load:', error);
                      },
                    });

                    // Load selected candidates for Step 2+
                    dataService.getSelectedCandidates(reviewId).subscribe({
                      next: (licenses) => {
                        patchState(store, {
                          selectedCandidates: licenses,
                          selectedCandidatesLoading: false,
                        });
                      },
                      error: (error) => {
                        console.error('[Selected Candidates] Failed to load:', error);
                      },
                    });

                    // Load confirmed candidates with GL contact info for Renewed Certificates table
                    dataService.getConfirmedCandidates(reviewId).subscribe({
                      next: (result) => {
                        patchState(store, {
                          confirmedCandidates: result.candidates,
                          confirmedCandidatesLoading: false,
                        });
                      },
                      error: (error) => {
                        console.error('[Confirmed Candidates] Failed to load:', error);
                      },
                    });
                  }
                },
                error: (_error: unknown) => {
                  // Even if reload fails, the confirmation was successful
                  patchState(store, {
                    activeReviewLoading: false,
                    currentStep: 4,
                  });
                },
              })
            );
          }),
          catchError((error: unknown) => {
            const message = (error as Error)?.message || 'Failed to confirm selection';
            patchState(store, {
              activeReviewLoading: false,
            });
            toastService.displayErrorMessage(message);
            return EMPTY;
          })
        )
      ),

      // Load selected candidates (for Step 2+ when returning to the workflow)
      loadSelectedCandidates: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { selectedCandidatesLoading: true })),
          switchMap((reviewId: string) => {
            return dataService.getSelectedCandidates(reviewId).pipe(
              tapResponse({
                next: (candidates) => {
                  patchState(store, {
                    selectedCandidates: candidates,
                    selectedCandidatesLoading: false,
                  });
                },
                error: (error: unknown) => {
                  const message = (error as Error)?.message || 'Failed to load selected candidates';
                  patchState(store, {
                    selectedCandidatesLoading: false,
                  });
                  toastService.displayErrorMessage(message);
                },
              })
            );
          })
        )
      ),

      // Load confirmed candidates with GL contact info (for Renewed Certificates table)
      loadConfirmedCandidates: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { confirmedCandidatesLoading: true })),
          switchMap((reviewId: string) => {
            return dataService.getConfirmedCandidates(reviewId).pipe(
              tapResponse({
                next: (result) => {
                  patchState(store, {
                    confirmedCandidates: result.candidates,
                    confirmedCandidatesLoading: false,
                  });
                },
                error: (error: unknown) => {
                  const message = (error as Error)?.message || 'Failed to load confirmed candidates';
                  patchState(store, {
                    confirmedCandidatesLoading: false,
                  });
                  toastService.displayErrorMessage(message);
                },
              })
            );
          })
        )
      ),

      // Load certificate recipients
      loadCertificateRecipients: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { certificateRecipientsLoading: true })),
          switchMap(() => {
            const reviewId = store.activeReview()?.id;
            if (!reviewId) {
              return of({ error: 'No active review' });
            }

            return dataService.getCertificateRecipients(reviewId).pipe(
              tapResponse({
                next: (contacts) => {
                  // Auto-select contacts that are already tagged as CertificateRecipient
                  const autoSelectedIds = contacts.filter((c) => c.isCertificateRecipient).map((c) => c.id);

                  patchState(store, {
                    certificateRecipients: contacts,
                    selectedContactIds: autoSelectedIds,
                    certificateRecipientsLoading: false,
                  });
                },
                error: (error: unknown) => {
                  const message = (error as Error)?.message || 'Failed to load contacts';
                  patchState(store, {
                    certificateRecipientsLoading: false,
                  });
                  toastService.displayErrorMessage(message);
                },
              })
            );
          })
        )
      ),

      // Toggle contact selection
      toggleContactSelection: (contactId: string) => {
        const currentSelection = store.selectedContactIds();
        let newSelection: string[];

        if (currentSelection.includes(contactId)) {
          newSelection = currentSelection.filter((id) => id !== contactId);
        } else {
          newSelection = [...currentSelection, contactId];
        }

        patchState(store, {
          selectedContactIds: newSelection,
        });
      },

      // Clear contact selection
      clearContactSelection: () => {
        patchState(store, {
          selectedContactIds: [],
        });
      },

      // Set selected contact IDs directly (batch update)
      setSelectedContactIds: (contactIds: string[]) => {
        patchState(store, {
          selectedContactIds: contactIds,
        });
      },

      // Select all certificate recipients
      selectAllCertificateRecipients: () => {
        const allContactIds = store
          .certificateRecipients()
          .filter((c) => c.email) // Only select contacts with email
          .map((c) => c.id);
        patchState(store, {
          selectedContactIds: allContactIds,
        });
      },

      // Notify GLs about new certificates
      notifyGLs: rxMethod<string[]>(
        pipe(
          tap(() => patchState(store, { isExecuting: true })),
          switchMap((licenseIds) => {
            const reviewId = store.activeReview()?.id;

            if (!reviewId) {
              return of({ error: 'No active review' });
            }

            // Check if this is a skip (empty array)
            if (licenseIds.length === 0) {
              // Skip GL notification - still call notifyCertificateAvailability with empty array to trigger state transition
              return dataService
                .notifyCertificateAvailability(reviewId, {
                  specificLicenseIds: [],
                })
                .pipe(
                  switchMap(() => {
                    // After skipping, reload both review and selectedCandidates
                    // to get updated state and glNotificationStatus
                    return forkJoin({
                      review: dataService.getReview(reviewId),
                      candidates: dataService.getSelectedCandidates(reviewId),
                    }).pipe(
                      tapResponse({
                        next: ({ review: updatedReview, candidates: updatedCandidates }) => {
                          const rn1Result = extractRn1Result(updatedReview);
                          patchState(store, {
                            activeReview: updatedReview,
                            selectedCandidates: updatedCandidates,
                            isExecuting: false,
                            glNotificationSent: true, // Mark as sent to allow progression
                            glNotificationResult: {
                              glsNotified: 0,
                              certificatesCount: 0,
                            },
                            currentStep: getStepFromState(updatedReview.currentState),
                            rn1CreationResult: rn1Result,
                            rn1CreationCompleted: !!rn1Result,
                          });
                        },
                        error: (_error: unknown) => {
                          // Even if reload fails, the skip was successful
                          patchState(store, {
                            isExecuting: false,
                            glNotificationSent: true,
                            glNotificationResult: {
                              glsNotified: 0,
                              certificatesCount: 0,
                            },
                            currentStep: 6, // Move to Final Review step
                          });
                        },
                      })
                    );
                  }),
                  catchError((error: unknown) => {
                    const message = (error as Error)?.message || 'Failed to skip GL notification';
                    patchState(store, {
                      isExecuting: false,
                    });
                    toastService.displayErrorMessage(message);
                    return EMPTY;
                  })
                );
            }

            // Normal GL notification flow
            return dataService
              .notifyCertificateAvailability(reviewId, {
                specificLicenseIds: licenseIds,
              })
              .pipe(
                switchMap((result) => {
                  // After sending GL notifications, reload both review and selectedCandidates
                  // to get updated state and glNotificationStatus
                  return forkJoin({
                    review: dataService.getReview(reviewId),
                    candidates: dataService.getSelectedCandidates(reviewId),
                  }).pipe(
                    tapResponse({
                      next: ({ review: updatedReview, candidates: updatedCandidates }) => {
                        const rn1Result = extractRn1Result(updatedReview);
                        patchState(store, {
                          activeReview: updatedReview,
                          selectedCandidates: updatedCandidates,
                          isExecuting: false,
                          glNotificationSent: true,
                          glNotificationResult: {
                            glsNotified: result.emailsSent,
                            certificatesCount: licenseIds.length,
                          },
                          rn1CreationResult: rn1Result,
                          rn1CreationCompleted: !!rn1Result,
                          currentStep: getStepFromState(updatedReview.currentState),
                        });
                        toastService.displaySuccessMessage(
                          `Sent ${result.emailsSent} GL notification${result.emailsSent !== 1 ? 's' : ''} for ${licenseIds.length} certificate${licenseIds.length !== 1 ? 's' : ''}`
                        );
                      },
                      error: (_error: unknown) => {
                        // Even if reload fails, the notification was successful
                        patchState(store, {
                          isExecuting: false,
                          glNotificationSent: true,
                          glNotificationResult: {
                            glsNotified: result.emailsSent,
                            certificatesCount: licenseIds.length,
                          },
                          currentStep: 6, // Move to Final Review step
                        });
                        toastService.displaySuccessMessage(
                          `Sent ${result.emailsSent} GL notification${result.emailsSent !== 1 ? 's' : ''} for ${licenseIds.length} certificate${licenseIds.length !== 1 ? 's' : ''}`
                        );
                      },
                    })
                  );
                }),
                catchError((error: unknown) => {
                  const message = (error as Error)?.message || 'Failed to notify GLs';
                  patchState(store, {
                    isExecuting: false,
                  });
                  toastService.displayErrorMessage(message);
                  return EMPTY;
                })
              );
          })
        )
      ),

      // Create RN1 inspections for next year
      createRn1Inspections: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { rn1CreationInProgress: true })),
          switchMap(() => {
            const reviewId = store.activeReview()?.id;
            if (!reviewId) {
              return of({ error: 'No active review' });
            }

            return dataService.createRn1Inspections(reviewId).pipe(
              switchMap((result) => {
                // Reload review to get updated state
                return dataService.getReview(reviewId).pipe(
                  tapResponse({
                    next: (updatedReview) => {
                      const rn1Result = extractRn1Result(updatedReview);
                      patchState(store, {
                        activeReview: updatedReview,
                        rn1CreationInProgress: false,
                        rn1CreationCompleted: true,
                        rn1CreationResult: rn1Result || result,
                        currentStep: getStepFromState(updatedReview.currentState),
                      });

                      const successMessage =
                        result.successCount > 0
                          ? `Created ${result.successCount} RN1 inspection${result.successCount > 1 ? 's' : ''} for next year`
                          : 'No RN1 inspections were created';

                      if (result.failureCount > 0) {
                        toastService.displaySuccessMessage(
                          `${successMessage}. ${result.failureCount} failed - see details below.`
                        );
                      } else {
                        toastService.displaySuccessMessage(successMessage);
                      }
                    },
                    error: (_error: unknown) => {
                      // Even if reload fails, the creation was successful
                      patchState(store, {
                        rn1CreationInProgress: false,
                        rn1CreationCompleted: true,
                        rn1CreationResult: result,
                      });

                      toastService.displaySuccessMessage(
                        `Created ${result.successCount} RN1 inspections for next year`
                      );
                    },
                  })
                );
              }),
              catchError((error: unknown) => {
                const message = (error as Error)?.message || 'Failed to create RN1 inspections';
                patchState(store, {
                  rn1CreationInProgress: false,
                });
                toastService.displayErrorMessage(message);
                return EMPTY;
              })
            );
          })
        )
      ),

      // Skip RN1 creation
      skipRn1Creation: rxMethod<string | undefined>(
        pipe(
          tap(() => patchState(store, { rn1CreationInProgress: true })),
          switchMap((reason) => {
            const reviewId = store.activeReview()?.id;
            if (!reviewId) {
              return of({ error: 'No active review' });
            }

            return dataService.skipRn1Creation(reviewId, reason).pipe(
              switchMap(() => {
                // Reload review to get updated state
                return dataService.getReview(reviewId).pipe(
                  tapResponse({
                    next: (updatedReview) => {
                      patchState(store, {
                        activeReview: updatedReview,
                        rn1CreationInProgress: false,
                        rn1CreationCompleted: false,
                        currentStep: getStepFromState(updatedReview.currentState),
                      });
                      toastService.displaySuccessMessage('RN1 inspection creation skipped. Proceeding to final review.');
                    },
                    error: (_error: unknown) => {
                      patchState(store, {
                        rn1CreationInProgress: false,
                      });
                      toastService.displaySuccessMessage('RN1 inspection creation skipped');
                    },
                  })
                );
              }),
              catchError((error: unknown) => {
                const message = (error as Error)?.message || 'Failed to skip RN1 creation';
                patchState(store, {
                  rn1CreationInProgress: false,
                });
                toastService.displayErrorMessage(message);
                return EMPTY;
              })
            );
          })
        )
      ),

      // Complete review
      completeReview: rxMethod<string | undefined>(
        pipe(
          tap(() => patchState(store, { activeReviewLoading: true })),
          switchMap((notes) => {
            const reviewId = store.activeReview()?.id;
            if (!reviewId) {
              return of({ error: 'No active review' });
            }

            return dataService.completeReview(reviewId, notes).pipe(
              switchMap((_result) => {
                // After completing, reload the review to get the updated state
                return dataService.getReview(reviewId).pipe(
                  tapResponse({
                    next: (updatedReview) => {
                      const rn1Result = extractRn1Result(updatedReview);
                      patchState(store, {
                        activeReview: updatedReview,
                        activeReviewLoading: false,
                        rn1CreationResult: rn1Result,
                        rn1CreationCompleted: !!rn1Result,
                        currentStep: getStepFromState(updatedReview.currentState),
                      });
                      toastService.displaySuccessMessage('License Renewal completed successfully');
                    },
                    error: (_error: unknown) => {
                      // Even if reload fails, the completion was successful
                      patchState(store, {
                        activeReviewLoading: false,
                        currentStep: 6,
                      });
                      toastService.displaySuccessMessage('License Renewal completed successfully');
                    },
                  })
                );
              }),
              catchError((error: unknown) => {
                const message = (error as Error)?.message || 'Failed to complete review';
                patchState(store, {
                  activeReviewLoading: false,
                });
                toastService.displayErrorMessage(message);
                return EMPTY;
              })
            );
          })
        )
      ),

      // Cancel review
      cancelReview: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { activeReviewLoading: true })),
          switchMap((reason) => {
            const reviewId = store.activeReview()?.id;
            if (!reviewId) {
              return of({ error: 'No active review' });
            }

            return dataService.cancelReview(reviewId, reason).pipe(
              tapResponse({
                next: () => {
                  patchState(store, {
                    activeReviewLoading: false,
                    activeReview: null,
                    showCancelDialog: false,
                  });
                  toastService.displaySuccessMessage('Review cancelled');
                },
                error: (error: unknown) => {
                  const message = (error as Error)?.message || 'Failed to cancel review';
                  patchState(store, {
                    activeReviewLoading: false,
                  });
                  toastService.displayErrorMessage(message);
                },
              })
            );
          })
        )
      ),

      // UI state methods
      setCurrentStep: (step: number) => {
        patchState(store, { currentStep: step });
      },

      openCreateDialog: () => {
        patchState(store, { showCreateDialog: true });
      },

      closeCreateDialog: () => {
        patchState(store, { showCreateDialog: false });
      },

      openCancelDialog: () => {
        patchState(store, { showCancelDialog: true });
      },

      closeCancelDialog: () => {
        patchState(store, { showCancelDialog: false });
      },

      clearUnsavedChanges: () => {
        patchState(store, { unsavedChanges: false });
      },

      resetStore: () => {
        patchState(store, initialState);
      },

      clearLastCreatedReviewId: () => {
        patchState(store, { lastCreatedReviewId: null });
      },

      // REMOVED: loadProcessedLicenses - use selectedCandidates instead

      // Load certificate generation status
      loadCertificateGenerationStatus: rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, {
              certificateGenerationStatusLoading: true,
            })
          ),
          switchMap((reviewId) =>
            dataService.getCertificateGenerationStatus(reviewId).pipe(
              tapResponse({
                next: (status) => {
                  const previousStatus = store.certificateGenerationStatus();
                  const allComplete =
                    status.totalCandidates > 0 &&
                    status.completedCount + status.failedCount === status.totalCandidates;

                  // Show toast when all certificates are done (and this is a transition)
                  if (
                    allComplete &&
                    previousStatus &&
                    previousStatus.completedCount + previousStatus.failedCount < previousStatus.totalCandidates
                  ) {
                    if (status.failedCount === 0) {
                      toastService.displaySuccessMessage(
                        `All ${status.completedCount} certificates generated successfully!`
                      );
                    } else {
                      toastService.displaySuccessMessage(
                        `Certificate generation complete: ${status.completedCount} succeeded, ${status.failedCount} failed`
                      );
                    }
                  }

                  patchState(store, {
                    certificateGenerationStatus: status,
                    certificateGenerationStatusLoading: false,
                  });

                  // Load selected candidates separately with OData $select to get only needed fields
                  dataService.getSelectedCandidates(reviewId).subscribe({
                    next: (licenses) => {
                      patchState(store, {
                        selectedCandidates: licenses,
                        selectedCandidatesLoading: false,
                      });
                    },
                    error: (error) => {
                      console.error('[Selected Candidates] Failed to load:', error);
                      patchState(store, {
                        selectedCandidatesLoading: false,
                      });
                    },
                  });
                },
                error: (error: unknown) => {
                  console.error('Failed to load certificate generation status:', error);
                  patchState(store, {
                    certificateGenerationStatusLoading: false,
                  });
                },
              })
            )
          )
        )
      ),

      // Retry failed certificate generation
      retryCertificateGeneration: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isExecuting: true })),
          switchMap((candidateId) => {
            const reviewId = store.activeReview()?.id;
            if (!reviewId) {
              return of({ error: 'No active review' });
            }

            return dataService.retryFailedCertificateGeneration(reviewId, candidateId).pipe(
              switchMap(() => {
                // After retry, refresh processed licenses
                return dataService.getCandidates(reviewId).pipe(
                  tapResponse({
                    next: (candidates) => {
                      const selectedCandidates = candidates.filter((c) => c.isSelected);

                      patchState(store, {
                        selectedCandidates,
                        isExecuting: false,
                      });
                      toastService.displaySuccessMessage('Certificate generation retried successfully');
                    },
                    error: (error: unknown) => {
                      const message = (error as Error)?.message || 'Failed to refresh after retry';
                      patchState(store, {
                        isExecuting: false,
                      });
                      toastService.displayErrorMessage(message);
                    },
                  })
                );
              }),
              catchError((error: unknown) => {
                const message = (error as Error)?.message || 'Failed to retry certificate generation';
                patchState(store, {
                  isExecuting: false,
                });
                toastService.displayErrorMessage(message);
                return EMPTY;
              })
            );
          })
        )
      ),

      // Retry failed GL notification
      retryGlNotification: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isExecuting: true })),
          switchMap((candidateId) => {
            const reviewId = store.activeReview()?.id;
            if (!reviewId) {
              return of({ error: 'No active review' });
            }

            return dataService.retryFailedGlNotification(reviewId, candidateId).pipe(
              switchMap(() => {
                // After retry, refresh processed licenses
                return dataService.getCandidates(reviewId).pipe(
                  tapResponse({
                    next: (candidates) => {
                      const selectedCandidates = candidates.filter((c) => c.isSelected);

                      patchState(store, {
                        selectedCandidates,
                        isExecuting: false,
                      });
                      toastService.displaySuccessMessage('GL notification retried successfully');
                    },
                    error: (error: unknown) => {
                      const message = (error as Error)?.message || 'Failed to refresh after retry';
                      patchState(store, {
                        isExecuting: false,
                      });
                      toastService.displayErrorMessage(message);
                    },
                  })
                );
              }),
              catchError((error: unknown) => {
                const message = (error as Error)?.message || 'Failed to retry GL notification';
                patchState(store, {
                  isExecuting: false,
                });
                toastService.displayErrorMessage(message);
                return EMPTY;
              })
            );
          })
        )
      ),
    })
  ),
  withHooks((store, destroyRef = inject(DestroyRef), dataService = inject(LicenseRenewalWorkflowDataService)) => {
    return {
      onInit() {
        console.info('[License Renewal Workflow] Store initialized');

        // Auto-refresh processed licenses and certificate status every 5 seconds
        // when in IssuingCertificates or FinalReview state
        interval(5000)
          .pipe(
            switchMap(() => {
              const review = store.activeReview();
              const reviewId = review?.id;
              const state = review?.currentState;
              const status = store.certificateGenerationStatus();

              // Only poll when in GL Notification or Final Review step
              const shouldPoll = reviewId &&
                (state === 'IssuingCertificates' || state === 'FinalReview');

              // Check if there are pending/processing certificates
              const hasPending = status &&
                (status.pendingCount > 0 || status.processingCount > 0 || status.queuedCount > 0);

              if (shouldPoll && hasPending) {
                // Refresh certificate status and processed licenses
                dataService.getCertificateGenerationStatus(reviewId).subscribe({
                  next: (status) => {
                    patchState(store, {
                      certificateGenerationStatus: status,
                      certificateGenerationStatusLoading: false,
                    });
                  },
                  error: (error) => {
                    console.error('[Certificate Status] Failed to load during polling:', error);
                  },
                });

                dataService.getSelectedCandidates(reviewId).subscribe({
                  next: (licenses) => {
                    patchState(store, {
                      selectedCandidates: licenses,
                      selectedCandidatesLoading: false,
                    });
                  },
                  error: (error) => {
                    console.error('[Selected Candidates] Failed to load during polling:', error);
                  },
                });
              }

              return EMPTY;
            }),
            takeUntilDestroyed(destroyRef)
          )
          .subscribe();
      },
      onDestroy() {
        console.info('[License Renewal Workflow] Store destroyed');
        store.resetStore();
      },
    };
  })
);
